import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { ApiCallError, callFunction } from '@/lib/api';
import { inviteAcceptErrorI18nKey, recoveryRedirectLocation } from '@/lib/authFormErrors';
import { PortalAuthShell } from '@/components/portal/PortalAuthShell';

interface AcceptPatientPortalOutput {
  user_id: string;
  email: string;
  patient_id: string;
  clinic_id: string;
  session: {
    access_token: string;
    refresh_token: string;
  };
}

export function PortalAcceptInvitation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const recoveryTo = recoveryRedirectLocation(location.pathname, location.search, location.hash);
  const token = params.get('token');

  if (recoveryTo) {
    return <Navigate to={recoveryTo} replace />;
  }

  if (!token) {
    return (
      <PortalAuthShell
        clinicName="Clinio"
        title={t('portal.accept.title')}
        subtitle={t('portal.accept.subtitle')}
        under={
          <Link to="/portal/login" className="link">
            {t('portal.accept.back_to_login')}
          </Link>
        }
      >
        <div className="card auth-card">
          <p className="help" style={{ color: 'var(--red-ink)' }}>
            {t('portal.accept.missing_token')}
          </p>
        </div>
      </PortalAuthShell>
    );
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);

    if (password.length < 8) {
      setError(t('auth.reset_password.min_length'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.reset_password.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const result = await callFunction<
        { token: string; password: string },
        AcceptPatientPortalOutput
      >('accept-patient-portal', { token, password });

      const { error: setErr } = await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });
      if (setErr) throw setErr;

      navigate('/portal/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiCallError) {
        setError(t(inviteAcceptErrorI18nKey(err.code, 'portal')));
      } else {
        setError(t('auth.errors.unknown'));
      }
      setSubmitting(false);
    }
  };

  return (
    <PortalAuthShell
      clinicName="Clinio"
      title={t('portal.accept.title')}
      subtitle={t('portal.accept.subtitle')}
      under={
        <Link to="/portal/login" className="link">
          {t('portal.accept.back_to_login')}
        </Link>
      }
    >
      <div className="card auth-card">
        {error ? (
          <p className="help" style={{ color: 'var(--red-ink)', marginBottom: 12 }}>
            {error}
          </p>
        ) : null}
        <form className="stack" onSubmit={(event) => void handleSubmit(event)}>
          <div className="field">
            <label htmlFor="pw">{t('portal.accept.password')}</label>
            <input
              id="pw"
              className="input"
              type="password"
              name="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <p className="help">{t('portal.accept.password_hint')}</p>
          </div>
          <div className="field">
            <label htmlFor="pw2">{t('portal.accept.password_confirm')}</label>
            <input
              id="pw2"
              className="input"
              type="password"
              name="passwordConfirm"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </div>
          <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting}>
            {submitting ? t('portal.accept.submitting') : t('portal.accept.submit')}
          </button>
        </form>
      </div>
    </PortalAuthShell>
  );
}
